import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingActionBar from './FloatingActionBar';
import SimpleLanguageSelector from './SimpleLanguageSelector';
import BusinessSelector from './BusinessSelector';
import VoiceAssistantShell from './VoiceAssistantShell';
import UniversalSearch from './UniversalSearch/UniversalSearch';
import { buildNavigationItems } from './layoutNavigation';
import useVoiceRecordingCleanup from '../hooks/useVoiceRecordingCleanup';
import { requestMicrophoneAccess } from '../utils/mobileAudioCapture';
import { isVoiceCaptureUiDisabled } from '../voice/killSwitches';
import { useAssistantViewport } from '../hooks/useAssistantViewport';
import { useGlobalVoiceAssistant } from '../hooks/useGlobalVoiceAssistant';
import { useAssistantPageContext } from '../hooks/useDashboardAssistantContext';
import { resolveNavigationSegment } from '../constants/assistantSuggestedQueries';
import { useHybridAuth } from '../contexts/HybridAuthContext';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
  user: any;
  business: any;
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, business, onLogout }) => {
  const { switchBusiness, refreshSharedData } = useHybridAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(new Set(['accountant'])); // Default to expanded
  const location = useLocation();
  const navigate = useNavigate();
  const { t, ready } = useTranslation();
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  
  // Global voice recording cleanup on navigation
  useVoiceRecordingCleanup();
  
  // Global Voice Assistant
  const voiceAssistant = useGlobalVoiceAssistant();
  const isAssistantDesktop = useAssistantViewport();
  const assistantDisplayMode = voiceAssistant.displayMode;
  const isDashboard = location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
  const navigationKey = resolveNavigationSegment(location.pathname);
  const pageName = t(`navigation.${navigationKey}`, { defaultValue: navigationKey });
  const assistantContext = useAssistantPageContext(
    location.pathname,
    location.search,
    pageName,
    isDashboard,
    t,
    business
  );

  useEffect(() => {
    const gvaState = ((window as any).__GVA_STATE__ ||= {});
    gvaState.assistantContext = assistantContext;
  }, [assistantContext]);

  useEffect(() => {
    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    };

    window.addEventListener('keydown', handleSearchShortcut);
    return () => window.removeEventListener('keydown', handleSearchShortcut);
  }, []);

  // Handle clicks outside profile dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setProfileDropdownOpen(false);
        setSidebarOpen(false);
      }
    };

    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [profileDropdownOpen]);

  // Setup Global Voice Assistant event listeners
  useEffect(() => {
    const cleanupTranscript = voiceAssistant.setupTranscriptListener();
    const cleanupRecording = voiceAssistant.setupRecordingListener();
    
    return () => {
      cleanupTranscript();
      cleanupRecording();
    };
  }, [voiceAssistant.setupRecordingListener, voiceAssistant.setupTranscriptListener]);

  // Handle voice navigation events from the global voice assistant
  useEffect(() => {
    const handleVoiceNavigation = (event: CustomEvent) => {
      const { route } = event.detail;
      console.log('🎯 Voice navigation event received:', route);
      if (route) {
        navigate(route);
      }
    };

    window.addEventListener('voiceNavigation', handleVoiceNavigation as EventListener);
    
    return () => {
      window.removeEventListener('voiceNavigation', handleVoiceNavigation as EventListener);
    };
  }, [navigate]);

  // Refresh shared auth data when voice commands mutate contacts or accounts
  useEffect(() => {
    const handleVoiceDataRefresh = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const resources: string[] = detail.resources || [];
      if (resources.includes('contacts') || resources.includes('accounts')) {
        void refreshSharedData();
      }
    };

    window.addEventListener('voiceDataRefresh', handleVoiceDataRefresh as EventListener);
    return () => window.removeEventListener('voiceDataRefresh', handleVoiceDataRefresh as EventListener);
  }, [refreshSharedData]);

  // Don't render until i18n is ready
  if (!ready) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading translations...</p>
      </div>
    );
  }

  // Listen for enhanced voice interface trigger

  // Check if user has accountant role
  const isAccountant = user?.role === 'accountant' || user?.role === 'admin';

  // Check if inventory is enabled (this could be a setting or feature flag)
  const isInventoryEnabled = true; // This could be made dynamic based on business settings

  // Check if user is superadmin
  const isSuperAdmin = user?.is_super_admin;

  const isNavActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/');
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navigationItems = buildNavigationItems({
    t,
    isAccountant,
    isInventoryEnabled,
    isSuperAdmin,
  });

  const toggleSubmenu = (submenuKey: string) => {
    setExpandedSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(submenuKey)) {
        newSet.delete(submenuKey);
      } else {
        newSet.add(submenuKey);
      }
      return newSet;
    });
  };

  const handleImageUpload = () => {
    navigate('/image-processing');
  };

  const handleJournalEntries = () => {
    navigate('/journal-entries');
  };

  const handleContacts = () => {
    navigate('/contacts');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const handleMicPress = () => {
    if (isVoiceCaptureUiDisabled()) return;

    if (voiceAssistant.isRecording) {
      voiceAssistant.stopVoiceInput();
      return;
    }

    // Start mic access in the same tap turn — required on iOS Safari.
    const micPromise = requestMicrophoneAccess();

    if (!voiceAssistant.isOpen) {
      voiceAssistant.openAssistant();
    }

    void micPromise.then((stream) => {
      voiceAssistant.startVoiceInput(stream);
    });
  };

  const voiceCaptureEnabled = !isVoiceCaptureUiDisabled();

  return (
    <div className="layout" data-assistant-display-mode={assistantDisplayMode}>
      {/* Top Navbar */}
      <nav className="top-navbar" aria-label="Main navigation">
        <div className="navbar-left">
          <button
            type="button"
            className="navbar-icon-btn mobile-menu-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={sidebarOpen}
          >
            <span className="material-icons" aria-hidden="true">menu</span>
          </button>
          <div className="navbar-brand">
            <span className="navbar-brand-name">EZaccounting</span>
            <span className="navbar-brand-tagline">{t('auth.subtitle')}</span>
          </div>
          <button
            type="button"
            className="universal-search-trigger"
            onClick={() => setSearchOpen(true)}
            aria-label={t('search.ariaLabel')}
          >
            <span className="material-icons" aria-hidden="true">search</span>
            <span className="universal-search-trigger-text">{t('search.triggerLabel')}</span>
            <span className="universal-search-trigger-kbd" aria-hidden="true">
              <kbd>⌘</kbd><kbd>K</kbd>
            </span>
          </button>
        </div>
        <div className="navbar-right">
          <button
            type="button"
            className="navbar-icon-btn universal-search-mobile-btn"
            onClick={() => setSearchOpen(true)}
            aria-label={t('search.ariaLabel')}
          >
            <span className="material-icons" aria-hidden="true">search</span>
          </button>
          <SimpleLanguageSelector />
          
          {/* Business Information Badge */}
          {business && (
            <div className="business-info-badge">
              <span className="badge-text">
                {business.name} | {business.base_currency_code} | {business.country_code}
              </span>
            </div>
          )}
            
          {/* User Profile Dropdown */}
          <div className="user-profile-dropdown" ref={profileDropdownRef}>
            <button
              type="button"
              className="navbar-icon-btn user-profile-btn"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              aria-label="Open profile menu"
              aria-expanded={profileDropdownOpen}
            >
              <span className="material-icons user-profile-icon" aria-hidden="true">account_circle</span>
              <span className="user-name">{user?.first_name || 'Admin'}</span>
            </button>
            
            {profileDropdownOpen && (
              <div className="profile-dropdown-menu">
                <div className="profile-dropdown-header">
                  <div className="profile-avatar" aria-hidden="true">
                    <span className="material-icons">person</span>
                  </div>
                  <div className="profile-info">
                    <div className="profile-name">{user?.first_name} {user?.last_name}</div>
                    <div className="profile-email">{user?.email}</div>
                    {user?.role && <div className="profile-role-badge">{user.role}</div>}
                  </div>
                </div>

                <div className="profile-dropdown-section">
                  <div className="profile-section-label">{t('navigation.organization', 'Organization')}</div>
                  <BusinessSelector
                    variant="profile"
                    onBusinessSelect={async (selectedBusiness) => {
                      if (selectedBusiness && selectedBusiness.id !== business?.id) {
                        try {
                          await switchBusiness(selectedBusiness.id.toString());
                        } catch (error) {
                          console.error('Failed to switch business:', error);
                        }
                      }
                    }}
                    currentBusiness={business}
                  />
                </div>

                <nav className="profile-dropdown-nav" aria-label="Account menu">
                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={() => {
                      navigate('/profile');
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <span className="material-icons" aria-hidden="true">person_outline</span>
                    {t('navigation.profile', 'Profile')}
                  </button>

                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={() => {
                      navigate('/settings');
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <span className="material-icons" aria-hidden="true">settings</span>
                    {t('settings.title')}
                  </button>
                </nav>

                <div className="profile-dropdown-footer">
                  <button
                    type="button"
                    className="profile-menu-item logout"
                    onClick={() => {
                      onLogout();
                      setProfileDropdownOpen(false);
                    }}
                  >
                    <span className="material-icons" aria-hidden="true">logout</span>
                    {t('auth.logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Layout Content */}
      <div className="layout-content">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header" style={{ position: 'relative' }}>
            <h2>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ marginRight: '8px', verticalAlign: 'middle', cursor: 'pointer' }}
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                role="button"
                aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <title>{sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}</title>
                {sidebarCollapsed ? (
                  // Hamburger (open)
                  <>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <line x1="3" y1="12" x2="21" y2="12"/>
                    <line x1="3" y1="18" x2="21" y2="18"/>
                  </>
                ) : (
                  // Close (X) when sidebar is open
                  <>
                    <line x1="5" y1="5" x2="19" y2="19"/>
                    <line x1="19" y1="5" x2="5" y2="19"/>
                  </>
                )}
              </svg>
              EZaccounting.AI
            </h2>
            {/* When collapsed, show a small always-visible hamburger at the same spot */}
            {sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsed(false)}
                aria-label="Expand sidebar"
                title="Expand sidebar"
                style={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          
          <nav className="sidebar-nav">
            {navigationItems.map((item) => {
              // Handle submenu items
              if (item.submenu) {
                const submenuKey = item.label.toLowerCase().replace(/\s+/g, '-');
                const isExpanded = expandedSubmenus.has(submenuKey);
                
                return (
                  <div key={item.label} className="nav-group">
                    <div 
                      className="nav-group-header clickable" 
                      onClick={() => toggleSubmenu(submenuKey)}
                      title={sidebarCollapsed ? item.label : ''}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                      <span className={`nav-arrow ${isExpanded ? 'expanded' : ''}`}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="6,9 12,15 18,9"/>
                        </svg>
                      </span>
                    </div>
                    <div className={`nav-submenu ${isExpanded ? 'expanded' : 'collapsed'}`}>
                      {item.submenu.map((subItem) => (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          className={`nav-subitem ${isNavActive(subItem.path) ? 'active' : ''}`}
                          onClick={() => setSidebarOpen(false)}
                          title={sidebarCollapsed ? subItem.label : ''}
                        >
                          <span className="nav-icon">{subItem.icon}</span>
                          <span className="nav-label">{subItem.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              
              // Handle regular navigation items
              if (!item.path) {
                return null;
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${isNavActive(item.path) ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </Link>
              );
            })}
          </nav>

        </div>

        {/* Main Content */}
        <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <main className="content">
            {children}
          </main>
        </div>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile/tablet bottom action bar — hidden on desktop where the assistant rail replaces it */}
      {!isAssistantDesktop && (
        <FloatingActionBar
          onDashboard={handleDashboard}
          onImageUpload={handleImageUpload}
          onJournalEntries={handleJournalEntries}
          onContacts={handleContacts}
          onMicPress={voiceCaptureEnabled ? handleMicPress : undefined}
          isVoiceRecording={voiceAssistant.isRecording}
        />
      )}

      <VoiceAssistantShell
        displayMode={assistantDisplayMode}
        onCollapseRail={voiceAssistant.toggleRail}
        onOpenAssistant={voiceAssistant.openAssistant}
        onFabMicPress={voiceCaptureEnabled ? handleMicPress : undefined}
        messages={voiceAssistant.messages}
        isProcessing={voiceAssistant.isProcessing}
        isRecording={voiceAssistant.isRecording}
        activeComponent={voiceAssistant.activeComponent}
        componentData={voiceAssistant.componentData}
        showHistoryModal={voiceAssistant.showHistoryModal}
        pastSessions={voiceAssistant.pastSessions}
        selectedPastSession={voiceAssistant.selectedPastSession}
        isMinimized={voiceAssistant.isMinimized}
        currentTranscript={voiceAssistant.currentTranscript}
        confirmCard={voiceAssistant.confirmCard}
        quickPicks={voiceAssistant.quickPicks}
        dynamicSuggestedQueries={voiceAssistant.dynamicSuggestedQueries}
        pageSuggestedQueries={voiceAssistant.pageSuggestedQueries}
        onQuickPickSelect={voiceAssistant.handleQuickPickSelect}
        onClose={voiceAssistant.closeAssistant}
        onMinimize={voiceAssistant.minimizeAssistant}
        onMaximize={voiceAssistant.maximizeAssistant}
        onStartVoiceInput={voiceCaptureEnabled ? voiceAssistant.startVoiceInput : () => undefined}
        onStopVoiceInput={voiceAssistant.stopVoiceInput}
        onTranscription={voiceAssistant.handleTranscription}
        onProcessingChange={voiceAssistant.handleProcessingChange}
        onOpenHistory={voiceAssistant.handleOpenHistory}
        onCloseHistoryModal={voiceAssistant.handleCloseHistoryModal}
        onSelectPastSession={voiceAssistant.handleSelectPastSession}
        onLoadSession={voiceAssistant.handleLoadSession}
        onLoadSelectedSession={voiceAssistant.handleLoadSelectedSession}
        onRewindToMessage={voiceAssistant.handleRewindToMessage}
        onStartNewChat={voiceAssistant.handleStartNewChat}
        onClearHistory={voiceAssistant.handleClearHistory}
        onCloseComponent={voiceAssistant.handleCloseComponent}
        onConfirmTransaction={voiceAssistant.handleConfirmTransaction}
        onConfirmQuotation={voiceAssistant.handleConfirmQuotation}
        assistantContext={assistantContext}
        company={business}
        notify={(message, type) => console.log('Notification:', message, type)}
      />

      <UniversalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

    </div>
  );
};

export default Layout;
